import React, { useState } from 'react';
import { useApp } from '../AppContext';

interface QuizOption { text: string; value: keyof typeof initialScores; }
interface QuizQuestion { question: string; options: QuizOption[]; }

const initialScores = { Individuale: 0, Coppia: 0, Genitoriale: 0, Giovani: 0 };

const QUIZ: QuizQuestion[] = [
  {
    question: 'Cosa ti porta a considerare un supporto psicologico in questo momento?',
    options: [
      { text: 'Difficoltà a gestire ansia, tensioni quotidiane o continui attacchi di panico.', value: 'Individuale' },
      { text: 'Ripetuti scontri, allontanamento o incomprensioni con il mio partner.', value: 'Coppia' },
      { text: 'Fatica nel comprendere e gestire le reazioni ed i comportamenti dei miei figli.', value: 'Genitoriale' },
      { text: 'Un senso generale di insicurezza e smarrimento sul mio futuro accademico o lavorativo.', value: 'Giovani' },
    ],
  },
  {
    question: 'In quale contesto relazionale senti di faticare maggiormente?',
    options: [
      { text: 'Nel rapporto intimo con me stesso, pensieri autosabotanti e scarsa autostima.', value: 'Individuale' },
      { text: 'Nella sfera di coppia, sento che non parliamo più la stessa lingua.', value: 'Coppia' },
      { text: "All'interno del nucleo familiare (genitori/figli), le tensioni sono costanti.", value: 'Genitoriale' },
      { text: "Nel mondo del lavoro o dell'università, fatico a impormi e ad emergere.", value: 'Giovani' },
    ],
  },
  {
    question: 'Se dovessi definire il tuo obiettivo prioritario adesso, quale sceglieresti?',
    options: [
      { text: 'Trovare pace interiore, stabilità emotiva e serenità.', value: 'Individuale' },
      { text: 'Salvare il legame affettivo o decidere con rispetto la via della separazione.', value: 'Coppia' },
      { text: 'Costruire un metodo educativo che riduca la tensione a casa.', value: 'Genitoriale' },
      { text: 'Trovare la mia strada professionale con sicurezza ed energia.', value: 'Giovani' },
    ],
  },
  {
    question: 'Preferiresti svolgere i tuoi colloqui con quale frequenza indicativa?',
    options: [
      { text: 'Una volta alla settimana o ogni 15 giorni per un lavoro profondo personale.', value: 'Individuale' },
      { text: 'In coppia con il mio partner, per un lavoro condiviso e congiunto.', value: 'Coppia' },
      { text: 'Incontri focalizzati per ricevere risposte ed indicazioni educative pratiche.', value: 'Genitoriale' },
      { text: 'Un pacchetto di orientamento di breve durata mirato ai miei prossimi obiettivi.', value: 'Giovani' },
    ],
  },
];

const RESULTS = {
  Individuale: {
    service: 'Psicoterapia Individuale',
    title: 'Supporto Individuale Continuo',
    desc: 'Le risposte indicano la necessità di uno spazio privato, un colloquio psicologico orientato al benessere della persona e alla gestione di ansia o stati di incertezza interiore.',
  },
  Coppia: {
    service: 'Terapia di Coppia',
    title: 'Terapia e Mediazione di Coppia',
    desc: 'Emergono bisogni legati alla comunicazione di coppia e alla gestione dei conflitti affettivi. Un percorso condiviso aiuterà a ricreare sintonia emotiva.',
  },
  Genitoriale: {
    service: 'Sostegno Genitoriale',
    title: 'Sostegno e Orientamento Genitoriale',
    desc: 'Le dinamiche familiari e genitoriali necessitano di una consulenza volta a ritrovare serenità e strategie educative assertive per gestire al meglio le sfide dei figli.',
  },
  Giovani: {
    service: 'Consulenza Giovani Adulti',
    title: 'Consulenza di Carriera & Identità',
    desc: "Consigliamo la consulenza focalizzata per giovani adulti, ideale per sbloccare l'ansia da prestazione accademica, universitaria o lavorativa.",
  },
};

export default function Quiz() {
  const { preselectService } = useApp();
  const [index, setIndex] = useState(0);
  const [scores, setScores] = useState(initialScores);
  const [showResult, setShowResult] = useState(false);

  const select = (value: keyof typeof initialScores) => {
    const next = { ...scores, [value]: scores[value] + 1 };
    if (index < QUIZ.length - 1) {
      setScores(next);
      setIndex(index + 1);
    } else {
      setScores(next);
      setShowResult(true);
    }
  };

  const restart = () => {
    setIndex(0);
    setScores(initialScores);
    setShowResult(false);
  };

  const winningKey = (Object.keys(scores) as Array<keyof typeof initialScores>)
    .reduce((max, k) => (scores[k] > scores[max] ? k : max), 'Individuale' as keyof typeof initialScores);
  const result = RESULTS[winningKey];

  const apply = () => {
    preselectService(result.service);
    setTimeout(() => {
      document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' });
    }, 40);
  };

  const q = QUIZ[index];
  const progress = ((index + 1) / QUIZ.length) * 100;

  return (
    <section id="test" className="py-24 bg-sageLight/35 relative overflow-hidden">
      <div className="test-blob" />
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="reveal bg-cream p-8 md:p-12 rounded-3xl border border-forest/10 shadow-lg space-y-8">
          <div className="text-center space-y-3">
            <span className="text-xs uppercase tracking-[0.25em] text-forest font-bold">Autovalutazione Gratuita</span>
            <h2 className="font-serif text-3xl md:text-4xl text-forestDark">Qual è il percorso più adatto a te?</h2>
            <p className="text-xs text-charcoal/60 max-w-lg mx-auto">Rispondi a 4 semplici domande per chiarire le tue attuali necessità psicologiche ed identificare l'orientamento di supporto ideale.</p>
          </div>

          <div className="space-y-6 pt-4">
            {!showResult && (
              <div className="space-y-4" key={index}>
                <div className="flex items-center justify-between text-xs text-sage font-bold">
                  <span>DOMANDA {index + 1} DI {QUIZ.length}</span>
                  <span className="w-24 h-1.5 bg-sageLight rounded-full overflow-hidden">
                    <span className="block h-full bg-clay" style={{ width: `${progress}%`, transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)' }} />
                  </span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-forestDark">{q.question}</h3>

                <div className="grid grid-cols-1 gap-3 pt-2">
                  {q.options.map((opt, i) => (
                    <button
                      key={opt.text}
                      type="button"
                      onClick={() => select(opt.value)}
                      className="quiz-opt p-4 bg-beigeBG hover:bg-sageLight/50 text-left rounded-xl border border-forest/5 text-sm font-medium smooth-transition text-forestDark"
                      style={{ ['--d' as any]: `${i * 70}ms` }}
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showResult && (
              <div className="text-center space-y-6 py-6">
                <div className="w-16 h-16 bg-sage/15 rounded-full flex items-center justify-center mx-auto text-forest animate-result-pop">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div className="space-y-2">
                  <span className="text-xs uppercase tracking-widest text-sage font-bold">Risultato Consigliato</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-forestDark">{result.title}</h3>
                  <p className="text-sm text-charcoal/80 max-w-md mx-auto leading-relaxed">{result.desc}</p>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                  <button onClick={apply} className="btn-shine bg-forest hover:bg-forestDark text-cream px-6 py-3 rounded-full text-sm font-medium smooth-transition hover:-translate-y-0.5">
                    Applica al modulo e Prenota
                  </button>
                  <button onClick={restart} className="text-xs uppercase tracking-widest text-sage hover:text-forest font-bold smooth-transition">
                    Ripeti il test
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
